/**
 * ★★ WO — "ESTE AGENDAMENTO ESTÁ PENDENTE?" Puro, e nasce aqui.
 *
 * Pendente = **a data já passou e ninguém deu baixa**. Não é cancelado, não é compareceu,
 * não é faltou: é consulta que aconteceu (ou não) e ficou sem registro.
 *
 * ★★ POR QUE ISTO É UM MÓDULO, e não duas linhas na página: o **lote 3** vai usar a MESMA
 * regra para decidir se o agendamento conta na meta (`metas/calculo.js`), e o painel do lote 2
 * usa para mostrar o aviso no card. Duas implementações da mesma pergunta é como esta casa
 * acabou com três definições de comparecimento e duas de data de desfecho.
 *
 * Medido em `docs/wo-medicao.md` §6: dos **183** agendamentos criados em setembro, **76** têm
 * data vencida e nenhuma baixa — 41%. ★ É por isso que "agendou" é FÁCIL de inflar hoje: cria
 * e nunca resolve.
 *
 * ★ `agora` entra por parâmetro: sem isso o teste dependeria do relógio da máquina, que é a
 * lição que a WN travou com guarda.
 */

/** Status que significam "alguém resolveu esta consulta". */
export const STATUS_COM_BAIXA = ['compareceu', 'faltou'];

/** `Timestamp | Date | string` → `Date`, ou null. */
const paraData = (v) => {
  if (!v) return null;
  if (typeof v.toDate === 'function') return v.toDate();
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** Teve baixa? (compareceu ou faltou — cancelado NÃO é baixa, é desistência) */
export const temBaixa = (agendamento) => STATUS_COM_BAIXA.includes(agendamento?.status);

/**
 * Está PENDENTE: `agendado`, com data no passado, sem baixa.
 *
 * ⚠ `cancelado` **não** é pendente — alguém resolveu, mesmo que para desmarcar.
 * ⚠ Data no FUTURO **não** é pendente — é agendamento normal, e por decisão do dono
 * (plano §H, resposta 2) ele **conta** na meta.
 */
export function estaPendente(agendamento, agora = new Date()) {
  if (agendamento?.status !== 'agendado') return false;
  const quando = paraData(agendamento?.dataHora);
  if (!quando) return false;
  return quando < agora;
}

/**
 * ★ Conta na meta de agendamentos?
 *
 * Sim quando teve baixa OU quando a data ainda está por vir. Pendente não conta — é a
 * decisão D2 do dono, e é o que tira o "cria e nunca resolve" da conta.
 *
 * ⚠ Medido: em setembro isto levaria **183 → 98** (87 com baixa + 11 futuros).
 */
export const contaNaMeta = (agendamento, agora = new Date()) =>
  temBaixa(agendamento) || (agendamento?.status === 'agendado' && !estaPendente(agendamento, agora));

/**
 * ★★ WO lote 3a — OS LEADS COM PENDÊNCIA, para o selo do card. Puro.
 *
 * ⚠⚠ FILTRA PELO AUTOR, e o número é a razão. Medido em produção (3/out):
 *
 * | | |
 * |---|---|
 * | agendamentos pendentes | **757** |
 * | leads distintos com pendência | 694 |
 * | ★ desses, em etapa ABERTA (aparecem no kanban) | **623 de 1.345 — 46% da tela** |
 * | ⚠ quantos são da "Importação planilha 2026" | ★★ **626** |
 * | ★ da EQUIPE | ★ **57 (4%)** |
 *
 * ★ Um selo em **quase metade** dos cards não é destaque — é ruído, e a equipe aprende a
 * ignorá-lo no primeiro dia. ⚠ E o pedido do selo é um CHAMADO À AÇÃO ("vá dar baixa");
 * ninguém vai dar baixa retroativa em 626 consultas importadas do sistema antigo.
 *
 * ★★ É a QUARTA vez nesta peça que o mesmo filtro decide se o número é útil: 757→59 nos
 * agendamentos sem baixa, 254→41 nos registros de domingo, o campo de follow-up que não
 * existia, e agora 623→57 no selo.
 *
 * ★ Para contar TUDO (inclusive o importado), passe `idsDaEquipe` vazio ou `null` —
 * é de uma linha em quem chama.
 */
export function leadsComPendencia(agendamentos = [], { idsDaEquipe = null, agora = new Date() } = {}) {
  const daEquipe = idsDaEquipe instanceof Set ? idsDaEquipe : (idsDaEquipe ? new Set(idsDaEquipe) : null);
  const saida = new Set();
  for (const a of agendamentos ?? []) {
    if (!a?.leadId || !estaPendente(a, agora)) continue;
    if (daEquipe && daEquipe.size > 0 && !daEquipe.has(a.agendadoPorId)) continue;
    saida.add(a.leadId);
  }
  return saida;
}
